import {keysStore as platformUserKeysStore} from "@mytour/platform";
import type {EinvoicingStatusResponse} from "@mytour/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import {einvoicingApiService} from "./einvoicingApi.service.js";

/**
 * Log d'erreur mutation sans l'objet AxiosError brut (#651) : ce dernier
 * porte `config.data` (payload de requête, ici `phoneNumber`) et
 * `config.headers.Authorization` (le bearer JWT posé par l'intercepteur
 * axios) — jamais en clair dans une console, même locale.
 */
function logMutationError(context: string, error: unknown): void {
  if (axios.isAxiosError(error)) {
    console.error(context, {status: error.response?.status, message: error.message});
  } else {
    console.error(context, error);
  }
}

export const einvoicingKeysStore = {
  root: ["einvoicing"] as const,
  status: () => [...einvoicingKeysStore.root, "status"] as const,
};

/**
 * Donnée volatile qui pilote la timeline (#651, décision Hugo) : jamais de
 * cache CÔTÉ FRONT, `refetchOnMount: "always"` + `staleTime: 0` re-vérifient
 * le statut à chaque arrivée sur un composant qui consomme ce hook
 * (`EinvoicingSettingsPage`, et le nudge #650 via `AppModals`) — un GET de
 * plus par montage est voulu, pas un coût à éviter. Ce GET n'atteint pas
 * toujours Pennylane : le scan amont est mémoïsé côté client backend sur une
 * fenêtre courte (`PennylaneProvisioningClient`, #1110). Le poll récurrent
 * de fond (hors visite) reste hors scope, prévu par #652.
 */
export function useEinvoicingStatus(enabled = true) {
  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: einvoicingKeysStore.status(),
    queryFn: einvoicingApiService.getStatus,
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    staleTime: 0,
  });

  return {
    einvoicingStatus: data,
    isEinvoicingStatusLoading: isLoading,
    isEinvoicingStatusFetching: isFetching,
    refetchEinvoicingStatus: refetch,
  };
}

export function useUpdateEinvoicingChoice() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: einvoicingApiService.updateChoice,
    onSuccess(status) {
      queryClient.setQueryData(einvoicingKeysStore.status(), status);
    },
    onError(error) {
      console.error("Failed to update einvoicing choice:", error);
    },
  });
  return {updateEinvoicingChoice: mutateAsync, isUpdatingEinvoicingChoice: isPending};
}

/**
 * Création du compte Pennylane délégué (#651). La réponse n'expose que les
 * champs de confirmation (`CreatePennylaneAccountResponse`), pas le statut
 * complet — on invalide plutôt que `setQueryData` pour récupérer `choice`/
 * `blockers` recalculés côté serveur (`EinvoicingSettingsPage` en dépend pour
 * basculer sur la timeline). Un siret/raison sociale édités dans la modal
 * récap sont aussi re-persistés dans `CompanyLegalSetting` côté serveur
 * (#1034, décision #771) — invalide aussi le cache `company` (même pattern
 * que `useUpdateLegalIdentity`, `companySettingsQueries.ts`) pour que le
 * reste de l'app (autres onglets, mentions légales) ne serve pas une valeur
 * périmée après la création.
 */
export function useCreatePennylaneAccount() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: einvoicingApiService.createPennylaneAccount,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: einvoicingKeysStore.status()});
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    },
    onError(error) {
      logMutationError("Failed to create Pennylane account:", error);
    },
  });
  return {createPennylaneAccount: mutateAsync, isCreatingPennylaneAccount: isPending};
}

// Horodatage du clic "Plus tard" sur la modal de nudge au login (#650).
export function useMarkNudgeShown() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: einvoicingApiService.markNudgeShown,
    onSuccess(status) {
      /**
       * Merge CIBLÉ sur `nudgeLastShownAt` uniquement, jamais un remplacement du cache
       * par le snapshot entier — un remplacement complet écraserait un `choice` déjà
       * écrit entre-temps par une autre mutation avec la valeur périmée lue ici.
       */
      queryClient.setQueryData(
        einvoicingKeysStore.status(),
        (current: EinvoicingStatusResponse | undefined) =>
          current ? {...current, nudgeLastShownAt: status.nudgeLastShownAt} : status
      );
    },
    onError(error) {
      logMutationError("Failed to mark einvoicing nudge as shown:", error);
    },
  });
  return {markNudgeShown: mutateAsync, isMarkingNudgeShown: isPending};
}
